// AlertList — severity-colored alert list for stuck applicants, at-risk members, expiring gear, etc.

import { useState } from "react";

export interface AlertItem {
  id: string;
  title: string;
  subtitle?: string;
  severity: "critical" | "warning" | "info";
  meta?: string;
}

export interface AlertListProps {
  items: AlertItem[];
  title: string;
  emptyMessage?: string;
  isLoading?: boolean;
  maxVisible?: number;
}

const SEVERITY_BORDER: Record<AlertItem["severity"], string> = {
  critical: "border-l-status-red",
  warning: "border-l-status-amber",
  info: "border-l-[#3b82f6]",
};

const SEVERITY_BG: Record<AlertItem["severity"], string> = {
  critical: "bg-red-500/5",
  warning: "bg-amber-500/5",
  info: "bg-blue-500/5",
};

const SEVERITY_DOT: Record<AlertItem["severity"], string> = {
  critical: "bg-status-red",
  warning: "bg-status-amber",
  info: "bg-blue-500",
};

const SEVERITY_BADGE_BG: Record<AlertItem["severity"], string> = {
  critical: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  warning:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  info: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
};

function AlertRow({ item }: { item: AlertItem }) {
  return (
    <div
      className={`flex items-start gap-3 px-3 py-2.5 border-l-4 rounded-r-lg ${SEVERITY_BORDER[item.severity]} ${SEVERITY_BG[item.severity]}`}
    >
      {/* Dot */}
      <div className="mt-1 shrink-0">
        <div
          className={`w-2 h-2 rounded-full pulse-dot ${SEVERITY_DOT[item.severity]}`}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-theme-primary leading-snug truncate">
          {item.title}
        </p>
        {item.subtitle && (
          <p className="text-xs text-theme-muted mt-0.5 leading-snug truncate">
            {item.subtitle}
          </p>
        )}
      </div>

      {/* Meta */}
      {item.meta && (
        <span
          className={`shrink-0 text-xs font-medium px-1.5 py-0.5 rounded-md ${SEVERITY_BADGE_BG[item.severity]}`}
        >
          {item.meta}
        </span>
      )}
    </div>
  );
}

export default function AlertList({
  items,
  title,
  emptyMessage = "No alerts",
  isLoading = false,
  maxVisible = 10,
}: AlertListProps) {
  const [expanded, setExpanded] = useState(false);

  const visibleItems = expanded ? items : items.slice(0, maxVisible);
  const hasMore = items.length > maxVisible;

  // Count by severity for the badge summary
  const criticalCount = items.filter((i) => i.severity === "critical").length;

  if (isLoading) {
    return (
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b border-theme-subtle flex items-center justify-between">
          <div className="skeleton h-4 w-32 rounded-md" />
          <div className="skeleton h-5 w-8 rounded-full" />
        </div>
        <div className="p-4 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-12 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 border-b border-theme-subtle flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-theme-primary leading-tight">
          {title}
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          {criticalCount > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-500/15 text-status-red">
              {criticalCount} critical
            </span>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-theme-overlay text-theme-muted">
            {items.length}
          </span>
        </div>
      </div>

      {/* Items */}
      <div className="p-4 space-y-2">
        {items.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-theme-muted">{emptyMessage}</p>
          </div>
        ) : (
          <>
            {visibleItems.map((item) => (
              <AlertRow key={item.id} item={item} />
            ))}

            {/* Show all / collapse toggle */}
            {hasMore && (
              <button
                onClick={() => setExpanded((e) => !e)}
                className="w-full mt-1 text-xs font-semibold text-brand-adaptive hover:text-brand-300 py-1.5 rounded-lg hover:bg-theme-overlay transition-colors"
              >
                {expanded ? "Show less" : `Show all ${items.length} alerts`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
